/**
 * M3 + M5: Request creation, quote comparison & order placement (Purchasing)
 * CODA-1813 (M3) + CODA-1815 (M5)
 */
import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Send, ChevronRight, Calendar, Package, Building2, X, Check, ShoppingCart, BarChart3, Eye, AlertCircle, Settings, ThumbsUp, ThumbsDown } from 'lucide-react'
import type { ColumnDef } from '@tanstack/react-table'
import { apiClient } from '@/lib/api.ts'
import { useAuthStore } from '@/stores/auth.ts'
import {
  Button,
  Card,
  ConfirmDialog,
  DataTable,
  EmptyState,
  ErrorState,
  IconButton,
  Input,
  LoadingState,
  Modal,
  PageHeader,
  StatusBadge,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Textarea,
  useToast,
} from '@/components/ui'
import { useTenantStore } from '@/stores/tenants.ts'

// ─── Types ────────────────────────────────────────────────────────────────────

interface Rfq {
  id: string
  rfqNumber: number
  status: 'offen' | 'gesendet' | 'angebote_eingegangen' | 'frist_abgelaufen' | 'freigabe_ausstehend' | 'bestellt' | 'abgebrochen'
  deadlineDate: string
  notes: string | null
  itemCount: number
  supplierCount: number
  sentCount: number
  quoteCount: number
  createdAt: string
}

interface RfqItem {
  id: string
  itemName: string
  itemDescription: string | null
  quantity: string
  unit: string
  itemCode: string | null
  bedarfId: string | null
}

interface RfqSupplier {
  id: string
  supplierName: string
  tanssCompanyId: string | null
  contactName: string | null
  contactEmail: string
  token: string
  emailSentAt: string | null
  status: 'ausstehend' | 'gesendet' | 'angebot_erhalten' | 'abgelaufen' | 'bestellt'
  totalPrice?: number
  avgDeliveryDays?: number | null
  quoteCount?: number
  isComplete?: boolean
}

interface RfqQuote {
  rfqSupplierId: string
  rfqItemId: string
  unitPrice: number
  deliveryDays: number | null
  itemNote: string | null
}

interface QuoteMatrix {
  item: RfqItem
  supplierQuotes: Array<{
    supplierId: string
    supplierName: string
    unitPrice: number | null
    totalPrice: number | null
    deliveryDays: number | null
    itemNote: string | null
    hasQuote: boolean
  }>
}

interface RfqOrder {
  id: string
  rfqSupplierId: string
  sapTenantId: string
  sapCardCode: string
  sapPoDocEntry: number | null
  sapPoDocNum: number | null
  orderedAt: string
}

interface RfqDetail extends Rfq {
  items: RfqItem[]
  suppliers: RfqSupplier[]
  quotes: RfqQuote[]
  orders: RfqOrder[]
}

interface QuoteComparisonData {
  rfq: { id: string; rfqNumber: number; status: string }
  items: RfqItem[]
  suppliers: RfqSupplier[]
  matrix: QuoteMatrix[]
}

interface SapSupplier {
  CardCode: string
  CardName: string
  EmailAddress?: string
}

interface TanssCompany {
  id: number | string
  name: string
  email: string | null
}

interface TanssContact {
  id: number | string
  name?: string
  firstName?: string
  lastName?: string
  emailAddress?: string
  mail?: string
  email?: string
}

interface NewItem {
  itemName: string
  itemDescription: string
  quantity: number
  unit: string
  itemCode: string
  bedarfId: string | null
}

interface NewSupplier {
  supplierName: string
  tanssCompanyId: string | null
  contactName: string
  contactEmail: string
}

// ─── Status helpers ───────────────────────────────────────────────────────────

const STATUS_LABELS: Record<string, string> = {
  offen: 'Offen',
  gesendet: 'Gesendet',
  angebote_eingegangen: 'Angebote eingegangen',
  frist_abgelaufen: 'Frist abgelaufen',
  freigabe_ausstehend: 'Freigabe ausstehend',
  bestellt: 'Bestellt',
  abgebrochen: 'Abgebrochen',
}

const STATUS_MAP: Record<string, Parameters<typeof StatusBadge>[0]['status']> = {
  offen: 'pending',
  gesendet: 'info',
  angebote_eingegangen: 'success',
  frist_abgelaufen: 'warning',
  freigabe_ausstehend: 'warning',
  bestellt: 'active',
  abgebrochen: 'inactive',
}

const FILTER_TABS = [
  { key: 'all', label: 'Alle' },
  { key: 'offen', label: 'Offen' },
  { key: 'gesendet', label: 'Gesendet' },
  { key: 'angebote_eingegangen', label: 'Angebote eingegangen' },
  { key: 'frist_abgelaufen', label: 'Frist abgelaufen' },
  { key: 'freigabe_ausstehend', label: 'Freigabe ausstehend' },
  { key: 'bestellt', label: 'Bestellt' },
  { key: 'abgebrochen', label: 'Abgebrochen' },
]

function getContactEmail(c: TanssContact): string {
  return c.emailAddress ?? c.mail ?? c.email ?? ''
}

function contactDisplayName(c: TanssContact): string {
  if (c.name) return c.name
  if (c.firstName || c.lastName) return [c.firstName, c.lastName].filter(Boolean).join(' ')
  return `Kontakt ${c.id}`
}

function fmtPrice(n: number): string {
  return n.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €'
}

// ─── Quote Entry Modal ────────────────────────────────────────────────────────

interface QuoteEntryModalProps {
  rfqId: string
  supplier: RfqSupplier | null
  items: RfqItem[]
  existingQuotes: RfqQuote[]
  onClose: () => void
  onSaved: () => void
}

function QuoteEntryModal({ rfqId, supplier, items, existingQuotes, onClose, onSaved }: QuoteEntryModalProps) {
  const { toast } = useToast()

  const existingMap = new Map(existingQuotes.filter((q) => q.rfqSupplierId === supplier?.id).map((q) => [q.rfqItemId, q]))

  const [lines, setLines] = useState<Array<{ rfqItemId: string; unitPrice: string; deliveryDays: string; itemNote: string }>>(() =>
    items.map((item) => {
      const existing = existingMap.get(item.id)
      return {
        rfqItemId: item.id,
        unitPrice: existing ? String(existing.unitPrice) : '',
        deliveryDays: existing?.deliveryDays != null ? String(existing.deliveryDays) : '',
        itemNote: existing?.itemNote ?? '',
      }
    }),
  )

  const saveMutation = useMutation({
    mutationFn: () =>
      apiClient.post(`/api/einkauf/rfq/${rfqId}/quotes/${supplier!.id}`, {
        quotes: lines
          .filter((l) => l.unitPrice !== '')
          .map((l) => ({
            rfqItemId: l.rfqItemId,
            unitPrice: parseFloat(l.unitPrice) || 0,
            deliveryDays: l.deliveryDays ? parseInt(l.deliveryDays) : null,
            itemNote: l.itemNote.trim() || null,
          })),
      }).then((r) => r.data),
    onSuccess: () => {
      toast({ title: `Angebot von ${supplier?.supplierName} gespeichert`, tone: 'success' })
      onSaved()
      onClose()
    },
    onError: (err: Error) => toast({ title: 'Fehler beim Speichern', description: err.message, tone: 'danger' }),
  })

  function updateLine(i: number, field: string, value: string) {
    setLines((prev) => prev.map((l, idx) => idx === i ? { ...l, [field]: value } : l))
  }

  const hasAnyPrice = lines.some((l) => l.unitPrice !== '')

  return (
    <Modal open={!!supplier} onClose={onClose} title={`Angebot erfassen: ${supplier?.supplierName}`} size="xl"
      footer={
        <div className="flex justify-between w-full">
          <Button variant="ghost" onClick={onClose}>Abbrechen</Button>
          <Button variant="primary" onClick={() => saveMutation.mutate()} disabled={!hasAnyPrice || saveMutation.isPending}>
            <Check size={14} className="mr-1" /> {saveMutation.isPending ? 'Speichern…' : 'Angebot speichern'}
          </Button>
        </div>
      }
    >
      <p className="text-sm text-text-secondary mb-4">
        Geben Sie die erhaltenen Preise und Lieferzeiten für <strong>{supplier?.supplierName}</strong> ein.
      </p>
      <div className="space-y-3">
        {items.map((item, i) => (
          <div key={item.id} className="border border-border rounded-lg p-3">
            <div className="flex items-start justify-between mb-2">
              <div>
                <p className="text-sm font-medium">{item.itemName}</p>
                {item.itemDescription && <p className="text-xs text-text-secondary">{item.itemDescription}</p>}
                <p className="text-xs text-text-secondary">{parseFloat(item.quantity).toLocaleString('de-DE')} {item.unit}</p>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <Input
                label="Einzelpreis (€) *"
                type="number"
                min={0}
                step={0.01}
                value={lines[i]?.unitPrice ?? ''}
                onChange={(e) => updateLine(i, 'unitPrice', e.target.value)}
                placeholder="0,00"
              />
              <Input
                label="Lieferzeit (Werktage)"
                type="number"
                min={1}
                value={lines[i]?.deliveryDays ?? ''}
                onChange={(e) => updateLine(i, 'deliveryDays', e.target.value)}
                placeholder="z.B. 5"
              />
              <Input
                label="Notiz"
                value={lines[i]?.itemNote ?? ''}
                onChange={(e) => updateLine(i, 'itemNote', e.target.value)}
                placeholder="Optional"
              />
            </div>
            {lines[i]?.unitPrice && (
              <p className="text-xs text-text-secondary mt-1">
                Gesamtpreis: <strong>{fmtPrice(parseFloat(lines[i]!.unitPrice) * parseFloat(item.quantity))}</strong>
              </p>
            )}
          </div>
        ))}
      </div>
    </Modal>
  )
}

// ─── Order Dialog ─────────────────────────────────────────────────────────────

interface OrderDialogProps {
  rfqId: string
  rfqNumber: number
  open: boolean
  suppliers: RfqSupplier[]
  onClose: () => void
  onOrdered: (result: { sapPoDocNum?: number; warnings?: string[]; requiresApproval?: boolean; message?: string }) => void
}

function OrderDialog({ rfqId, rfqNumber, open, suppliers, onClose, onOrdered }: OrderDialogProps) {
  const { toast } = useToast()
  const tenants = useTenantStore((s) => s.tenants)
  const [selectedSupplierId, setSelectedSupplierId] = useState('')
  const [sapTenantId, setSapTenantId] = useState('')
  const [sapCardCode, setSapCardCode] = useState('')
  const [sapSearch, setSapSearch] = useState('')
  const [tanssTicketInput, setTanssTicketInput] = useState('')
  const [tanssTicketIds, setTanssTicketIds] = useState<string[]>([])
  const [notes, setNotes] = useState('')
  const [showConfirm, setShowConfirm] = useState(false)

  const { data: sapSuppliers } = useQuery({
    queryKey: ['sap-supplier-search', sapTenantId, sapSearch],
    queryFn: () => apiClient.get<SapSupplier[]>(`/api/einkauf/rfq/sap-suppliers?q=${encodeURIComponent(sapSearch)}&tenantId=${sapTenantId}`).then((r) => r.data),
    enabled: sapSearch.length >= 2 && !!sapTenantId,
    staleTime: 30_000,
  })

  const orderMutation = useMutation({
    mutationFn: () => apiClient.post(`/api/einkauf/rfq/${rfqId}/order`, {
      rfqSupplierId: selectedSupplierId,
      sapTenantId,
      sapCardCode,
      tanssTicketIds: tanssTicketIds.length > 0 ? tanssTicketIds : undefined,
      notes: notes.trim() || null,
    }).then((r) => r.data as { sapPoDocNum?: number; warnings?: string[]; requiresApproval?: boolean; message?: string }),
    onSuccess: (result) => {
      if (result.requiresApproval) {
        toast({ title: 'Freigabe angefordert', description: result.message, tone: 'warning' })
      } else {
        toast({ title: `Bestellung #${result.sapPoDocNum} erfolgreich angelegt!`, tone: 'success' })
      }
      onOrdered(result)
      onClose()
    },
    onError: (err: Error) => {
      toast({ title: 'Fehler bei der Bestellung', description: err.message, tone: 'danger' })
      setShowConfirm(false)
    },
  })

  function addTanssTicket() {
    const id = tanssTicketInput.trim()
    if (!id) return
    if (tanssTicketIds.includes(id)) return
    setTanssTicketIds((prev) => [...prev, id])
    setTanssTicketInput('')
  }

  const isValid = !!selectedSupplierId && !!sapTenantId && !!sapCardCode

  const selectedSupplier = suppliers.find((s) => s.id === selectedSupplierId)

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        title={`Bestellung auslösen — Anfrage #${rfqNumber}`}
        size="lg"
        footer={
          <div className="flex justify-between w-full">
            <Button variant="ghost" onClick={onClose}>Abbrechen</Button>
            <Button variant="primary" onClick={() => setShowConfirm(true)} disabled={!isValid}>
              <ShoppingCart size={14} className="mr-1" /> Bestellung prüfen & bestätigen
            </Button>
          </div>
        }
      >
        <div className="space-y-5">
          {/* Supplier selection */}
          <div>
            <p className="text-xs font-semibold text-text-secondary uppercase tracking-wide mb-2">1. Lieferant auswählen</p>
            <div className="space-y-2">
              {suppliers.filter((s) => s.status !== 'abgelaufen').map((s) => (
                <label key={s.id} className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                  selectedSupplierId === s.id ? 'border-brand-primary bg-brand-primary/5' : 'border-border hover:border-brand-primary/50'
                }`}>
                  <input
                    type="radio"
                    name="rfq-supplier"
                    value={s.id}
                    checked={selectedSupplierId === s.id}
                    onChange={() => setSelectedSupplierId(s.id)}
                    className="accent-brand-primary"
                  />
                  <div className="flex-1">
                    <p className="text-sm font-medium">{s.supplierName}</p>
                    <p className="text-xs text-text-secondary">
                      {s.quoteCount != null ? `${s.quoteCount} Position(en) bepreist` : ''}
                      {(s.totalPrice ?? 0) > 0 ? ` · Gesamt: ${fmtPrice(s.totalPrice ?? 0)}` : ''}
                      {s.avgDeliveryDays != null ? ` · ⌀ ${s.avgDeliveryDays} WT` : ''}
                    </p>
                  </div>
                  <StatusBadge
                    status={s.status === 'angebot_erhalten' ? 'success' : s.status === 'gesendet' ? 'info' : 'pending'}
                    label={s.status === 'angebot_erhalten' ? 'Angebot' : s.status === 'gesendet' ? 'Gesendet' : 'Ausstehend'}
                  />
                </label>
              ))}
            </div>
          </div>

          {/* SAP tenant */}
          <div>
            <p className="text-xs font-semibold text-text-secondary uppercase tracking-wide mb-2">2. SAP-Mandant</p>
            <select
              value={sapTenantId}
              onChange={(e) => { setSapTenantId(e.target.value); setSapCardCode(''); setSapSearch('') }}
              className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-primary/30"
            >
              <option value="">— Mandant wählen —</option>
              {tenants.map((t) => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </div>

          {/* SAP CardCode */}
          {sapTenantId && (
            <div>
              <p className="text-xs font-semibold text-text-secondary uppercase tracking-wide mb-2">3. SAP-Lieferant (CardCode)</p>
              <Input
                label="Lieferant in SAP suchen"
                value={sapSearch}
                onChange={(e) => { setSapSearch(e.target.value); setSapCardCode('') }}
                placeholder="Name oder CardCode eingeben…"
              />
              {sapCardCode && (
                <div className="mt-2 flex items-center gap-2 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
                  <Check size={14} className="text-green-600" />
                  <span className="text-sm font-medium text-green-800">{sapCardCode}</span>
                  <button onClick={() => { setSapCardCode(''); setSapSearch('') }} className="ml-auto text-green-600 hover:text-green-800">
                    <X size={12} />
                  </button>
                </div>
              )}
              {!sapCardCode && sapSuppliers && sapSuppliers.length > 0 && (
                <div className="mt-1 border border-border rounded-lg overflow-hidden">
                  {sapSuppliers.slice(0, 8).map((s) => (
                    <button
                      key={s.CardCode}
                      onClick={() => { setSapCardCode(s.CardCode); setSapSearch(s.CardName) }}
                      className="w-full flex items-center justify-between px-3 py-2 hover:bg-surface-secondary text-left border-b border-border last:border-0"
                    >
                      <div>
                        <span className="text-sm font-medium">{s.CardName}</span>
                        {s.EmailAddress && <span className="text-xs text-text-secondary ml-2">{s.EmailAddress}</span>}
                      </div>
                      <span className="text-xs font-mono text-text-tertiary">{s.CardCode}</span>
                    </button>
                  ))}
                </div>
              )}
              {!sapCardCode && sapSearch.length >= 2 && (!sapSuppliers || sapSuppliers.length === 0) && (
                <div className="mt-2">
                  <Input
                    label="Oder CardCode direkt eingeben"
                    value={sapCardCode}
                    onChange={(e) => setSapCardCode(e.target.value.toUpperCase())}
                    placeholder="z.B. L10001"
                  />
                </div>
              )}
            </div>
          )}

          {/* TANSS tickets */}
          <div>
            <p className="text-xs font-semibold text-text-secondary uppercase tracking-wide mb-2">4. TANSS-Ticket-IDs (optional)</p>
            <p className="text-xs text-text-secondary mb-2">Ein Kommentar mit Bestelldetails wird in diesen Tickets hinterlegt.</p>
            <div className="flex gap-2">
              <Input
                label=""
                value={tanssTicketInput}
                onChange={(e) => setTanssTicketInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTanssTicket() }}}
                placeholder="Ticket-ID eingeben + Enter"
                className="flex-1"
              />
              <Button variant="secondary" onClick={addTanssTicket} className="mt-0 self-end">+ Hinzufügen</Button>
            </div>
            {tanssTicketIds.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {tanssTicketIds.map((id) => (
                  <span key={id} className="inline-flex items-center gap-1 px-2 py-1 bg-surface-secondary rounded-full text-xs">
                    #{id}
                    <button onClick={() => setTanssTicketIds((prev) => prev.filter((t) => t !== id))}>
                      <X size={10} />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Notes */}
          <div>
            <Textarea
              label="Notizen (erscheinen in SAP-Beleg und Bestätigungsmail)"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder="z.B. Bitte Lieferung an Lager Berlin"
            />
          </div>
        </div>
      </Modal>

      <ConfirmDialog
        open={showConfirm}
        onCancel={() => setShowConfirm(false)}
        onConfirm={() => orderMutation.mutate()}
        title="Bestellung verbindlich auslösen?"
        message={
          <div className="space-y-2">
            <p>Folgende Aktionen werden ausgeführt:</p>
            <ul className="list-disc list-inside text-sm space-y-1 text-text-secondary">
              <li>SAP-Einkaufsbeleg wird bei <strong>{tenants.find((t) => t.id === sapTenantId)?.name}</strong> angelegt</li>
              <li>Lieferant: <strong>{selectedSupplier?.supplierName}</strong> (SAP: {sapCardCode})</li>
              <li>Bestätigungsmail an {selectedSupplier?.contactEmail}</li>
              {tanssTicketIds.length > 0 && <li>TANSS-Kommentar in {tanssTicketIds.length} Ticket(s)</li>}
            </ul>
            <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
              Diese Aktion kann nicht rückgängig gemacht werden.
            </p>
          </div>
        }
        confirmLabel={orderMutation.isPending ? 'Bestellung läuft…' : 'Jetzt bestellen'}
        variant="default"
        loading={orderMutation.isPending}
      />
    </>
  )
}

// ─── RFQ Detail Modal ─────────────────────────────────────────────────────────

interface RfqDetailModalProps {
  rfqId: string | null
  onClose: () => void
}

interface ApprovalStatus {
  id: string
  rfqSupplierId: string
  totalAmount: string
  thresholdAmount: string
  status: 'pending' | 'approved' | 'rejected'
  requestedByUserId: string
  approverUserId: string | null
  decisionAt: string | null
  decisionComment: string | null
  createdAt: string
  isApprover: boolean
}

function RfqDetailModal({ rfqId, onClose }: RfqDetailModalProps) {
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const currentUser = useAuthStore((s) => s.user)
  const [activeTab, setActiveTab] = useState('overview')
  const [quoteEntrySupplier, setQuoteEntrySupplier] = useState<RfqSupplier | null>(null)
  const [orderDialogOpen, setOrderDialogOpen] = useState(false)
  const [approvalComment, setApprovalComment] = useState('')
  const [showDecideConfirm, setShowDecideConfirm] = useState<'approve' | 'reject' | null>(null)

  const { data, isLoading, error } = useQuery({
    queryKey: ['rfq-detail', rfqId],
    queryFn: () => apiClient.get(`/api/einkauf/rfq/${rfqId}`).then((r) => r.data.data as RfqDetail),
    enabled: !!rfqId,
    staleTime: 30_000,
  })

  const { data: approvalData } = useQuery({
    queryKey: ['rfq-approval', rfqId],
    queryFn: () => apiClient.get(`/api/einkauf/rfq/${rfqId}/approval`).then((r) => r.data.approval as ApprovalStatus | null),
    enabled: !!rfqId && data?.status === 'freigabe_ausstehend',
    staleTime: 15_000,
  })

  const { data: comparison } = useQuery({
    queryKey: ['rfq-quotes', rfqId],
    queryFn: () => apiClient.get(`/api/einkauf/rfq/${rfqId}/quotes`).then((r) => r.data.data as QuoteComparisonData),
    enabled: !!rfqId && activeTab === 'quotes',
    staleTime: 30_000,
  })

  const sendMutation = useMutation({
    mutationFn: () => apiClient.post(`/api/einkauf/rfq/${rfqId}/send`, {}).then((r) => r.data),
    onSuccess: (result) => {
      toast({ title: `${result.sent}/${result.total} Emails versendet`, tone: 'success' })
      queryClient.invalidateQueries({ queryKey: ['rfq-detail', rfqId] })
      queryClient.invalidateQueries({ queryKey: ['rfq-list'] })
    },
    onError: (err: Error) => toast({ title: 'Fehler beim Senden', description: err.message, tone: 'danger' }),
  })

  const decideMutation = useMutation({
    mutationFn: (vars: { approvalId: string; decision: 'approved' | 'rejected' }) =>
      apiClient.post(`/api/einkauf/rfq/approvals/${vars.approvalId}/decide`, {
        decision: vars.decision,
        comment: approvalComment.trim() || undefined,
      }).then((r) => r.data as { success: boolean; decision: string; sapPoDocNum?: number }),
    onSuccess: (result) => {
      if (result.decision === 'approved') {
        toast({ title: `Freigabe erteilt — SAP-Bestellung #${result.sapPoDocNum} angelegt`, tone: 'success' })
      } else {
        toast({ title: 'Bestellung abgelehnt', tone: 'warning' })
      }
      setShowDecideConfirm(null)
      setApprovalComment('')
      queryClient.invalidateQueries({ queryKey: ['rfq-detail', rfqId] })
      queryClient.invalidateQueries({ queryKey: ['rfq-approval', rfqId] })
      queryClient.invalidateQueries({ queryKey: ['rfq-list'] })
    },
    onError: (err: Error) => toast({ title: 'Fehler bei der Entscheidung', description: err.message, tone: 'danger' }),
  })

  function handleQuotesSaved() {
    queryClient.invalidateQueries({ queryKey: ['rfq-detail', rfqId] })
    queryClient.invalidateQueries({ queryKey: ['rfq-quotes', rfqId] })
    queryClient.invalidateQueries({ queryKey: ['rfq-list'] })
  }

  function handleOrdered() {
    queryClient.invalidateQueries({ queryKey: ['rfq-detail', rfqId] })
    queryClient.invalidateQueries({ queryKey: ['rfq-approval', rfqId] })
    queryClient.invalidateQueries({ queryKey: ['rfq-list'] })
  }

  const canOrder = data && data.status !== 'bestellt' && data.status !== 'abgebrochen' && data.status !== 'freigabe_ausstehend'
  const isOrdered = data?.status === 'bestellt'
  const isApprovalPending = data?.status === 'freigabe_ausstehend'
  void currentUser

  return (
    <>
      <Modal
        open={!!rfqId}
        onClose={onClose}
        title={data ? `Anfrage #${data.rfqNumber}` : 'Anfrage laden…'}
        size="xl"
      >
        {isLoading && <LoadingState />}
        {error && <ErrorState title="Fehler beim Laden" description={(error as Error).message} />}
        {data && (
          <div className="space-y-4">
            {/* Status + actions */}
            <div className="flex items-center gap-3 flex-wrap">
              <StatusBadge status={STATUS_MAP[data.status] ?? 'pending'} label={STATUS_LABELS[data.status] ?? data.status} />
              <span className="text-sm text-text-secondary">
                Frist: <strong>{new Date(data.deadlineDate).toLocaleDateString('de-DE')}</strong>
              </span>
              <div className="ml-auto flex gap-2">
                {data.suppliers.some((s) => s.status === 'ausstehend') && (
                  <Button size="xs" variant="secondary" onClick={() => sendMutation.mutate()} disabled={sendMutation.isPending}>
                    <Send size={12} className="mr-1" />
                    {sendMutation.isPending ? 'Sende…' : 'Emails senden'}
                  </Button>
                )}
                {canOrder && !isOrdered && (
                  <Button size="xs" variant="primary" onClick={() => setOrderDialogOpen(true)}>
                    <ShoppingCart size={12} className="mr-1" /> Bestellung auslösen
                  </Button>
                )}
                {isOrdered && data.orders[0] && (
                  <span className="text-xs text-green-700 bg-green-50 border border-green-200 rounded-full px-3 py-1">
                    ✓ SAP-Bestellung #{data.orders[0].sapPoDocNum} angelegt
                  </span>
                )}
                {isApprovalPending && (
                  <span className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-3 py-1">
                    ⏳ Freigabe ausstehend
                  </span>
                )}
              </div>
            </div>

            {/* M6: Approval pending banner */}
            {isApprovalPending && approvalData && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 space-y-3">
                <div className="flex items-start gap-2">
                  <AlertCircle size={16} className="text-amber-600 mt-0.5 shrink-0" />
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-amber-800">Freigabe erforderlich</p>
                    <p className="text-xs text-amber-700 mt-0.5">
                      Bestellwert <strong>{parseFloat(approvalData.totalAmount).toLocaleString('de-DE', { minimumFractionDigits: 2 })} €</strong> überschreitet
                      den Schwellenwert von {parseFloat(approvalData.thresholdAmount).toLocaleString('de-DE', { minimumFractionDigits: 2 })} €.
                      Freigeber wurden per E-Mail benachrichtigt.
                    </p>
                  </div>
                </div>

                {approvalData.isApprover && approvalData.status === 'pending' && (
                  <div className="space-y-2 border-t border-amber-200 pt-3">
                    <p className="text-xs font-semibold text-amber-800">Ihre Entscheidung (Sie sind Freigeber):</p>
                    <Textarea
                      label="Kommentar (optional)"
                      value={approvalComment}
                      onChange={(e) => setApprovalComment(e.target.value)}
                      rows={2}
                      placeholder="Begründung für Genehmigung oder Ablehnung…"
                    />
                    <div className="flex gap-2">
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => setShowDecideConfirm('approve')}
                        disabled={decideMutation.isPending}
                      >
                        <ThumbsUp size={13} className="mr-1" /> Freigeben
                      </Button>
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => setShowDecideConfirm('reject')}
                        disabled={decideMutation.isPending}
                      >
                        <ThumbsDown size={13} className="mr-1" /> Ablehnen
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {data.notes && <p className="text-sm text-text-secondary bg-surface-secondary rounded p-3">{data.notes}</p>}

            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList>
                <TabsTrigger value="overview"><Eye size={13} className="mr-1" />Übersicht</TabsTrigger>
                <TabsTrigger value="quotes">
                  <BarChart3 size={13} className="mr-1" />Preisvergleich
                  {data.quoteCount > 0 && (
                    <span className="ml-1 px-1.5 py-0.5 bg-brand-primary/10 text-brand-primary text-xs rounded-full">{data.quoteCount}</span>
                  )}
                </TabsTrigger>
              </TabsList>

              {/* ── Overview Tab ── */}
              <TabsContent value="overview">
                <div className="space-y-4 pt-2">
                  <div>
                    <p className="text-xs font-semibold text-text-secondary uppercase tracking-wide mb-2">Positionen</p>
                    <div className="border border-border rounded-lg overflow-hidden">
                      {data.items.map((item, i) => (
                        <div key={item.id} className={`px-3 py-2 flex justify-between items-start ${i !== data.items.length - 1 ? 'border-b border-border' : ''}`}>
                          <div>
                            <p className="text-sm font-medium">{item.itemName}</p>
                            {item.itemDescription && <p className="text-xs text-text-secondary">{item.itemDescription}</p>}
                            {item.itemCode && <p className="text-xs text-text-tertiary font-mono">{item.itemCode}</p>}
                          </div>
                          <span className="text-sm text-text-secondary shrink-0 ml-3">
                            {parseFloat(item.quantity).toLocaleString('de-DE')} {item.unit}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <p className="text-xs font-semibold text-text-secondary uppercase tracking-wide">Lieferanten & Angebote</p>
                    </div>
                    <div className="border border-border rounded-lg overflow-hidden">
                      {data.suppliers.map((s, i) => (
                        <div key={s.id} className={`px-3 py-3 flex justify-between items-start ${i !== data.suppliers.length - 1 ? 'border-b border-border' : ''}`}>
                          <div className="flex-1">
                            <p className="text-sm font-medium">{s.supplierName}</p>
                            <p className="text-xs text-text-secondary">
                              {s.contactName ? `${s.contactName} · ` : ''}{s.contactEmail}
                            </p>
                            {s.emailSentAt && (
                              <p className="text-xs text-text-tertiary">
                                Gesendet: {new Date(s.emailSentAt).toLocaleString('de-DE')}
                              </p>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <StatusBadge
                              status={s.status === 'gesendet' ? 'info' : s.status === 'angebot_erhalten' ? 'success' : s.status === 'bestellt' ? 'active' : s.status === 'abgelaufen' ? 'inactive' : 'pending'}
                              label={s.status === 'gesendet' ? 'Gesendet' : s.status === 'angebot_erhalten' ? 'Angebot erhalten' : s.status === 'bestellt' ? 'Bestellt' : s.status === 'abgelaufen' ? 'Abgelaufen' : 'Ausstehend'}
                            />
                            {data.status !== 'bestellt' && data.status !== 'abgebrochen' && (
                              <Button size="xs" variant="ghost" onClick={() => setQuoteEntrySupplier(s)}>
                                Angebot eingeben
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </TabsContent>

              {/* ── Quotes Comparison Tab ── */}
              <TabsContent value="quotes">
                {!comparison && <LoadingState />}
                {comparison && (
                  <div className="pt-2">
                    {comparison.matrix.length === 0 && (
                      <EmptyState title="Noch keine Angebote" description="Tragen Sie Angebote über die Übersicht ein." />
                    )}
                    {comparison.matrix.length > 0 && (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm border-collapse">
                          <thead>
                            <tr className="bg-surface-secondary">
                              <th className="text-left px-3 py-2 font-semibold text-text-secondary border-b border-border">Position</th>
                              <th className="text-right px-3 py-2 font-semibold text-text-secondary border-b border-border">Menge</th>
                              {comparison.suppliers.map((s) => (
                                <th key={s.id} className="text-right px-3 py-2 border-b border-border min-w-[140px]">
                                  <div className="font-semibold text-text-primary">{s.supplierName}</div>
                                  {(s.totalPrice ?? 0) > 0 && (
                                    <div className="text-xs font-normal text-text-secondary">∑ {fmtPrice(s.totalPrice ?? 0)}</div>
                                  )}
                                  {s.avgDeliveryDays != null && (
                                    <div className="text-xs font-normal text-text-tertiary">⌀ {s.avgDeliveryDays} WT</div>
                                  )}
                                  {!s.isComplete && (
                                    <div className="text-xs text-amber-600 flex items-center justify-end gap-1">
                                      <AlertCircle size={10} /> unvollständig
                                    </div>
                                  )}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {comparison.matrix.map((row, ri) => {
                              // Find cheapest unit price for this item row
                              const prices = row.supplierQuotes.map((q) => q.unitPrice).filter((p) => p !== null) as number[]
                              const minPrice = prices.length > 0 ? Math.min(...prices) : null

                              return (
                                <tr key={row.item.id} className={ri % 2 === 0 ? 'bg-white' : 'bg-surface-secondary/40'}>
                                  <td className="px-3 py-2 border-b border-border">
                                    <p className="font-medium">{row.item.itemName}</p>
                                    {row.item.itemCode && <p className="text-xs text-text-tertiary font-mono">{row.item.itemCode}</p>}
                                  </td>
                                  <td className="px-3 py-2 text-right border-b border-border text-text-secondary">
                                    {parseFloat(row.item.quantity).toLocaleString('de-DE')} {row.item.unit}
                                  </td>
                                  {row.supplierQuotes.map((q) => {
                                    const isCheapest = q.unitPrice !== null && q.unitPrice === minPrice && prices.length > 1
                                    return (
                                      <td key={q.supplierId} className={`px-3 py-2 text-right border-b border-border ${isCheapest ? 'bg-green-50' : ''}`}>
                                        {q.hasQuote ? (
                                          <div>
                                            <p className={`font-medium ${isCheapest ? 'text-green-700' : ''}`}>
                                              {q.unitPrice !== null ? fmtPrice(q.unitPrice) : '—'}
                                              {isCheapest && <span className="ml-1 text-xs">✓</span>}
                                            </p>
                                            {q.totalPrice !== null && (
                                              <p className="text-xs text-text-secondary">={fmtPrice(q.totalPrice)}</p>
                                            )}
                                            {q.deliveryDays != null && (
                                              <p className="text-xs text-text-tertiary">{q.deliveryDays} WT</p>
                                            )}
                                            {q.itemNote && (
                                              <p className="text-xs text-text-tertiary italic truncate max-w-[120px]">{q.itemNote}</p>
                                            )}
                                          </div>
                                        ) : (
                                          <span className="text-text-tertiary text-xs italic">Kein Angebot</span>
                                        )}
                                      </td>
                                    )
                                  })}
                                </tr>
                              )
                            })}
                          </tbody>
                          <tfoot>
                            <tr className="font-semibold bg-surface-secondary">
                              <td colSpan={2} className="px-3 py-2 border-t-2 border-border">Gesamtpreis (netto)</td>
                              {comparison.suppliers.map((s) => (
                                <td key={s.id} className="px-3 py-2 text-right border-t-2 border-border">
                                  {(s.totalPrice ?? 0) > 0 ? fmtPrice(s.totalPrice ?? 0) : '—'}
                                </td>
                              ))}
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    )}

                    {/* Add/update quotes actions */}
                    {data.status !== 'bestellt' && data.status !== 'abgebrochen' && (
                      <div className="mt-3 flex flex-wrap gap-2">
                        {comparison.suppliers.map((s) => (
                          <Button
                            key={s.id}
                            size="xs"
                            variant="secondary"
                            onClick={() => {
                              const supplier = data.suppliers.find((ds) => ds.id === s.id)
                              if (supplier) { setQuoteEntrySupplier(supplier); setActiveTab('overview') }
                            }}
                          >
                            {s.isComplete ? '✓' : '+'} {s.supplierName}
                          </Button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </div>
        )}
      </Modal>

      {/* Quote entry sub-modal */}
      {data && quoteEntrySupplier && (
        <QuoteEntryModal
          rfqId={rfqId!}
          supplier={quoteEntrySupplier}
          items={data.items}
          existingQuotes={data.quotes}
          onClose={() => setQuoteEntrySupplier(null)}
          onSaved={handleQuotesSaved}
        />
      )}

      {/* Order dialog */}
      {data && orderDialogOpen && (
        <OrderDialog
          rfqId={rfqId!}
          rfqNumber={data.rfqNumber}
          open={orderDialogOpen}
          suppliers={data.suppliers}
          onClose={() => setOrderDialogOpen(false)}
          onOrdered={handleOrdered}
        />
      )}

      {/* Approval confirm dialogs */}
      {approvalData && showDecideConfirm === 'approve' && (
        <ConfirmDialog
          open
          onCancel={() => setShowDecideConfirm(null)}
          onConfirm={() => decideMutation.mutate({ approvalId: approvalData.id, decision: 'approved' })}
          title="Bestellung freigeben?"
          message={`Bestellwert: ${parseFloat(approvalData.totalAmount).toLocaleString('de-DE', { minimumFractionDigits: 2 })} € — nach Freigabe wird sofort ein SAP-Einkaufsbeleg angelegt.`}
          confirmLabel={decideMutation.isPending ? 'Wird verarbeitet…' : 'Freigeben & bestellen'}
          loading={decideMutation.isPending}
        />
      )}
      {approvalData && showDecideConfirm === 'reject' && (
        <ConfirmDialog
          open
          onCancel={() => setShowDecideConfirm(null)}
          onConfirm={() => decideMutation.mutate({ approvalId: approvalData.id, decision: 'rejected' })}
          title="Bestellung ablehnen?"
          message="Der Einkäufer wird per E-Mail benachrichtigt. Die Anfrage bleibt offen und kann erneut bearbeitet werden."
          confirmLabel={decideMutation.isPending ? 'Wird verarbeitet…' : 'Ablehnen'}
          variant="danger"
          loading={decideMutation.isPending}
        />
      )}
    </>
  )
}

// ─── Create Anfrage Wizard ────────────────────────────────────────────────────

interface CreateAnfrageDialogProps {
  open: boolean
  onClose: () => void
  onCreated: (rfqId: string, sendNow: boolean) => void
}

function CreateAnfrageDialog({ open, onClose, onCreated }: CreateAnfrageDialogProps) {
  const { toast } = useToast()
  const [step, setStep] = useState(1)

  // Step 1: Items
  const [items, setItems] = useState<NewItem[]>([
    { itemName: '', itemDescription: '', quantity: 1, unit: 'Stk', itemCode: '', bedarfId: null },
  ])

  // Step 2: Suppliers
  const [suppliers, setSuppliers] = useState<NewSupplier[]>([])
  const [tanssQuery, setTanssQuery] = useState('')
  const [selectedCompany, setSelectedCompany] = useState<TanssCompany | null>(null)
  const [manualEmail, setManualEmail] = useState('')
  const [manualName, setManualName] = useState('')
  const [manualCompanyName, setManualCompanyName] = useState('')
  const [addMode, setAddMode] = useState<'tanss' | 'manual'>('tanss')

  // Step 3: Deadline + send
  const [deadlineDate, setDeadlineDate] = useState('')
  const [notes, setNotes] = useState('')
  const [sendNow, setSendNow] = useState(true)

  const { data: deadlineDefaults } = useQuery({
    queryKey: ['rfq-default-deadline'],
    queryFn: () => apiClient.get('/api/einkauf/rfq/default-deadline').then((r) => r.data as { days: number; date: string }),
    enabled: open && step === 3,
    staleTime: 60_000,
  })

  if (deadlineDefaults?.date && !deadlineDate) {
    setDeadlineDate(deadlineDefaults.date)
  }

  // Settings: read contactCategory for TANSS filtering
  const { data: rfqSettings } = useQuery({
    queryKey: ['rfq-settings'],
    queryFn: () => apiClient.get('/api/einkauf/rfq/settings').then((r) => r.data as { deadlineDays: number; contactCategory: string }),
    staleTime: 300_000,
    enabled: open,
  })
  const contactCategory = rfqSettings?.contactCategory ?? 'Ansprechpartner Einkauf'

  const { data: tanssCompanies, isFetching: tanssLoading } = useQuery({
    queryKey: ['tanss-companies-search', tanssQuery],
    queryFn: () => apiClient.get(`/api/tanss/companies/search?q=${encodeURIComponent(tanssQuery)}`).then((r) => (r.data.data ?? r.data) as TanssCompany[]),
    enabled: tanssQuery.length >= 2 && addMode === 'tanss',
    staleTime: 30_000,
  })

  const { data: companyContactsResult } = useQuery<{ data: TanssContact[]; purchasingContactFound: boolean }>({
    queryKey: ['tanss-company-contacts', selectedCompany?.id, contactCategory],
    queryFn: () =>
      apiClient
        .get(`/api/tanss/companies/${selectedCompany!.id}/persons?purchasingCategory=${encodeURIComponent(contactCategory)}`)
        .then((r) => r.data as { data: TanssContact[]; purchasingContactFound: boolean }),
    enabled: !!selectedCompany,
    staleTime: 60_000,
  })
  const companyContacts = companyContactsResult?.data
  const purchasingContactFound = companyContactsResult?.purchasingContactFound

  const createMutation = useMutation({
    mutationFn: (payload: object) => apiClient.post('/api/einkauf/rfq', payload).then((r) => r.data.data as Rfq),
    onError: (err: Error) => toast({ title: 'Fehler beim Erstellen', description: err.message, tone: 'danger' }),
  })

  const sendMutation = useMutation({
    mutationFn: (id: string) => apiClient.post(`/api/einkauf/rfq/${id}/send`, {}).then((r) => r.data),
    onError: (err: Error) => toast({ title: 'Email-Versand fehlgeschlagen', description: err.message, tone: 'danger' }),
  })

  function resetDialog() {
    setStep(1)
    setItems([{ itemName: '', itemDescription: '', quantity: 1, unit: 'Stk', itemCode: '', bedarfId: null }])
    setSuppliers([])
    setTanssQuery('')
    setSelectedCompany(null)
    setManualEmail('')
    setManualName('')
    setManualCompanyName('')
    setDeadlineDate('')
    setNotes('')
    setSendNow(true)
  }

  function handleClose() {
    resetDialog()
    onClose()
  }

  function addItem() {
    setItems((prev) => [...prev, { itemName: '', itemDescription: '', quantity: 1, unit: 'Stk', itemCode: '', bedarfId: null }])
  }
  function removeItem(i: number) {
    setItems((prev) => prev.filter((_, idx) => idx !== i))
  }
  function updateItem(i: number, field: keyof NewItem, value: string | number | null) {
    setItems((prev) => prev.map((item, idx) => idx === i ? { ...item, [field]: value } : item))
  }

  function addContactAsSupplier(contact: TanssContact, company: TanssCompany) {
    const email = getContactEmail(contact)
    if (!email) {
      toast({ title: 'Kein E-Mail hinterlegt', description: 'Dieser Kontakt hat keine E-Mail-Adresse in TANSS.', tone: 'warning' })
      return
    }
    if (suppliers.some((s) => s.contactEmail === email)) {
      toast({ title: 'Bereits hinzugefügt', description: `${email} ist bereits in der Liste.`, tone: 'warning' })
      return
    }
    setSuppliers((prev) => [
      ...prev,
      { supplierName: company.name, tanssCompanyId: String(company.id), contactName: contactDisplayName(contact), contactEmail: email },
    ])
  }

  function addCompanyEmail(company: TanssCompany) {
    const email = company.email
    if (!email) {
      toast({ title: 'Keine Firmen-E-Mail', description: 'Diese Firma hat keine allgemeine E-Mail in TANSS.', tone: 'warning' })
      return
    }
    if (suppliers.some((s) => s.contactEmail === email)) {
      toast({ title: 'Bereits hinzugefügt', tone: 'warning' })
      return
    }
    setSuppliers((prev) => [
      ...prev,
      { supplierName: company.name, tanssCompanyId: String(company.id), contactName: '', contactEmail: email },
    ])
  }

  function addManualSupplier() {
    if (!manualEmail.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
      toast({ title: 'Ungültige E-Mail', description: 'Bitte geben Sie eine gültige E-Mail-Adresse ein.', tone: 'danger' })
      return
    }
    if (!manualCompanyName.trim()) {
      toast({ title: 'Firmenname erforderlich', tone: 'danger' })
      return
    }
    if (suppliers.some((s) => s.contactEmail === manualEmail)) {
      toast({ title: 'Bereits hinzugefügt', tone: 'warning' })
      return
    }
    setSuppliers((prev) => [
      ...prev,
      { supplierName: manualCompanyName.trim(), tanssCompanyId: null, contactName: manualName.trim(), contactEmail: manualEmail.trim() },
    ])
    setManualEmail('')
    setManualName('')
    setManualCompanyName('')
  }

  function removeSupplier(i: number) {
    setSuppliers((prev) => prev.filter((_, idx) => idx !== i))
  }

  async function handleCreate() {
    if (!deadlineDate) {
      toast({ title: 'Frist erforderlich', tone: 'danger' })
      return
    }

    const created = await createMutation.mutateAsync({
      deadlineDate,
      notes: notes.trim() || null,
      items: items.map((it) => ({
        itemName: it.itemName,
        itemDescription: it.itemDescription || null,
        quantity: it.quantity,
        unit: it.unit,
        itemCode: it.itemCode || null,
        bedarfId: it.bedarfId,
      })),
      suppliers: suppliers.map((s) => ({
        supplierName: s.supplierName,
        tanssCompanyId: s.tanssCompanyId,
        contactName: s.contactName || null,
        contactEmail: s.contactEmail,
      })),
    })

    if (sendNow) {
      await sendMutation.mutateAsync(created.id)
      toast({ title: `Anfrage #${created.rfqNumber} erstellt & Emails versendet`, tone: 'success' })
    } else {
      toast({ title: `Anfrage #${created.rfqNumber} erstellt`, tone: 'success' })
    }

    handleClose()
    onCreated(created.id, sendNow)
  }

  const step1Valid = items.length > 0 && items.every((it) => it.itemName.trim() !== '')
  const step2Valid = suppliers.length > 0
  const step3Valid = !!deadlineDate

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={`Neue Anfrage erstellen — Schritt ${step}/3`}
      size="xl"
      footer={
        <div className="flex justify-between w-full">
          <Button variant="ghost" onClick={step === 1 ? handleClose : () => setStep((s) => s - 1)}>
            {step === 1 ? 'Abbrechen' : '← Zurück'}
          </Button>
          {step < 3 ? (
            <Button variant="primary" onClick={() => setStep((s) => s + 1)} disabled={step === 1 ? !step1Valid : !step2Valid}>
              Weiter →
            </Button>
          ) : (
            <Button
              variant="primary"
              onClick={handleCreate}
              disabled={!step3Valid || createMutation.isPending || sendMutation.isPending}
            >
              {sendNow ? 'Anfrage erstellen & Emails senden' : 'Anfrage erstellen'}
            </Button>
          )}
        </div>
      }
    >
      {/* ── Step 1: Positionen ── */}
      {step === 1 && (
        <div className="space-y-4">
          <p className="text-sm text-text-secondary">Tragen Sie die anzufragenden Artikel/Positionen ein.</p>
          {items.map((item, i) => (
            <div key={i} className="border border-border rounded-lg p-4 space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-text-secondary">Position {i + 1}</span>
                {items.length > 1 && (
                  <IconButton icon={<X size={14} />} aria-label="Position entfernen" size="xs" variant="ghost" onClick={() => removeItem(i)} />
                )}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <Input
                    label="Artikel / Bezeichnung *"
                    value={item.itemName}
                    onChange={(e) => updateItem(i, 'itemName', e.target.value)}
                    placeholder="z.B. Cisco Switch Catalyst 9300"
                  />
                </div>
                <div className="col-span-2">
                  <Input
                    label="Beschreibung"
                    value={item.itemDescription}
                    onChange={(e) => updateItem(i, 'itemDescription', e.target.value)}
                    placeholder="z.B. 48-Port PoE+, Layer 3"
                  />
                </div>
                <Input
                  label="Menge *"
                  type="number"
                  min={0.01}
                  value={String(item.quantity)}
                  onChange={(e) => updateItem(i, 'quantity', parseFloat(e.target.value) || 1)}
                />
                <Input
                  label="Einheit"
                  value={item.unit}
                  onChange={(e) => updateItem(i, 'unit', e.target.value)}
                  placeholder="Stk"
                />
                <div className="col-span-2">
                  <Input
                    label="Artikelnummer (optional)"
                    value={item.itemCode}
                    onChange={(e) => updateItem(i, 'itemCode', e.target.value)}
                    placeholder="SAP-Artikelnummer oder Hersteller-PN"
                  />
                </div>
              </div>
            </div>
          ))}
          <Button variant="ghost" onClick={addItem} className="w-full">
            <Plus size={14} className="mr-1" /> Position hinzufügen
          </Button>
        </div>
      )}

      {/* ── Step 2: Lieferanten ── */}
      {step === 2 && (
        <div className="space-y-4">
          <p className="text-sm text-text-secondary">Wählen Sie Lieferanten aus TANSS oder fügen Sie manuell hinzu.</p>

          {suppliers.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs font-medium text-text-secondary uppercase tracking-wide">Ausgewählte Lieferanten ({suppliers.length})</p>
              {suppliers.map((s, i) => (
                <div key={i} className="flex items-center justify-between bg-surface-secondary rounded-lg px-3 py-2">
                  <div>
                    <p className="text-sm font-medium">{s.supplierName}</p>
                    <p className="text-xs text-text-secondary">{s.contactName ? `${s.contactName} · ` : ''}{s.contactEmail}</p>
                  </div>
                  <IconButton icon={<X size={14} />} aria-label="Entfernen" size="xs" variant="ghost" onClick={() => removeSupplier(i)} />
                </div>
              ))}
            </div>
          )}

          <div className="flex gap-2">
            <button
              onClick={() => setAddMode('tanss')}
              className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium border transition-colors ${addMode === 'tanss' ? 'bg-brand-primary text-white border-brand-primary' : 'bg-white text-text-secondary border-border hover:border-brand-primary'}`}
            >
              Aus TANSS suchen
            </button>
            <button
              onClick={() => setAddMode('manual')}
              className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium border transition-colors ${addMode === 'manual' ? 'bg-brand-primary text-white border-brand-primary' : 'bg-white text-text-secondary border-border hover:border-brand-primary'}`}
            >
              Manuell eingeben
            </button>
          </div>

          {addMode === 'tanss' && (
            <div className="space-y-3">
              <Input
                label="Firma in TANSS suchen"
                value={tanssQuery}
                onChange={(e) => { setTanssQuery(e.target.value); setSelectedCompany(null) }}
                placeholder="Mindestens 2 Zeichen eingeben…"
              />
              {tanssLoading && <p className="text-xs text-text-secondary">Suche läuft…</p>}
              {tanssCompanies && tanssCompanies.length > 0 && !selectedCompany && (
                <div className="border border-border rounded-lg overflow-hidden">
                  {tanssCompanies.slice(0, 8).map((c) => (
                    <button
                      key={c.id}
                      onClick={() => { setSelectedCompany(c); setTanssQuery(c.name) }}
                      className="w-full flex items-center gap-2 px-3 py-2 hover:bg-surface-secondary text-left border-b border-border last:border-0"
                    >
                      <Building2 size={14} className="text-text-tertiary shrink-0" />
                      <div>
                        <p className="text-sm font-medium">{c.name}</p>
                        {c.email && <p className="text-xs text-text-secondary">{c.email}</p>}
                      </div>
                    </button>
                  ))}
                </div>
              )}
              {selectedCompany && (
                <div className="border border-brand-primary/30 rounded-lg p-3 space-y-2">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-sm font-semibold">{selectedCompany.name}</p>
                      {selectedCompany.email && (
                        <button onClick={() => addCompanyEmail(selectedCompany)} className="text-xs text-brand-primary hover:underline">
                          + Firmen-E-Mail: {selectedCompany.email}
                        </button>
                      )}
                    </div>
                    <Button size="xs" variant="ghost" onClick={() => { setSelectedCompany(null); setTanssQuery('') }}>×</Button>
                  </div>
                  {companyContacts === undefined ? (
                    <p className="text-xs text-text-secondary">Lade Ansprechpartner…</p>
                  ) : companyContacts.length > 0 ? (
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <p className="text-xs text-text-secondary font-medium">
                          {purchasingContactFound === false
                            ? `Ansprechpartner (kein „${contactCategory}" in TANSS):`
                            : `Ansprechpartner (${contactCategory}):`}
                        </p>
                        {purchasingContactFound === false && (
                          <span className="text-xs bg-yellow-100 text-yellow-800 px-1.5 py-0.5 rounded">
                            Fallback: alle Kontakte
                          </span>
                        )}
                      </div>
                      {companyContacts.map((c) => {
                        const email = getContactEmail(c)
                        return (
                          <div key={c.id} className="flex items-center justify-between py-1">
                            <div>
                              <span className="text-sm">{contactDisplayName(c)}</span>
                              {email && <span className="text-xs text-text-secondary ml-2">{email}</span>}
                            </div>
                            <Button size="xs" variant="secondary" onClick={() => addContactAsSupplier(c, selectedCompany)} disabled={!email}>
                              + Hinzufügen
                            </Button>
                          </div>
                        )
                      })}
                    </div>
                  ) : (
                    <p className="text-xs text-text-secondary">Keine Ansprechpartner in TANSS hinterlegt.</p>
                  )}
                </div>
              )}
            </div>
          )}

          {addMode === 'manual' && (
            <div className="space-y-3 border border-border rounded-lg p-4">
              <Input
                label="Firmenname *"
                value={manualCompanyName}
                onChange={(e) => setManualCompanyName(e.target.value)}
                placeholder="Lieferant GmbH"
              />
              <Input
                label="Ansprechpartner (optional)"
                value={manualName}
                onChange={(e) => setManualName(e.target.value)}
                placeholder="Max Mustermann"
              />
              <Input
                label="E-Mail-Adresse *"
                type="email"
                value={manualEmail}
                onChange={(e) => setManualEmail(e.target.value)}
                placeholder="einkauf@lieferant.de"
              />
              <Button variant="secondary" onClick={addManualSupplier} disabled={!manualEmail || !manualCompanyName}>
                + Lieferant hinzufügen
              </Button>
            </div>
          )}
        </div>
      )}

      {/* ── Step 3: Frist & Absenden ── */}
      {step === 3 && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <Input
                label="Angebotsfrist *"
                type="date"
                value={deadlineDate}
                onChange={(e) => setDeadlineDate(e.target.value)}
                min={new Date().toISOString().slice(0, 10)}
              />
              {deadlineDefaults && (
                <p className="text-xs text-text-secondary mt-1">
                  Standard: {deadlineDefaults.days} Werktage ({deadlineDefaults.date})
                </p>
              )}
            </div>
            <div className="col-span-2">
              <Textarea
                label="Interne Notizen (optional)"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Interne Anmerkungen zur Anfrage…"
                rows={3}
              />
            </div>
          </div>

          <div className="bg-surface-secondary rounded-lg p-4 space-y-3">
            <p className="text-sm font-semibold">Zusammenfassung</p>
            <div className="flex items-center gap-2 text-sm">
              <Package size={14} className="text-text-tertiary" />
              <span>{items.length} Position{items.length !== 1 ? 'en' : ''}</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Building2 size={14} className="text-text-tertiary" />
              <span>{suppliers.length} Lieferant{suppliers.length !== 1 ? 'en' : ''}: {suppliers.map((s) => s.supplierName).join(', ')}</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Calendar size={14} className="text-text-tertiary" />
              <span>Frist: {deadlineDate ? new Date(deadlineDate).toLocaleDateString('de-DE') : '—'}</span>
            </div>
          </div>

          <div className="flex items-start gap-3 border border-border rounded-lg p-3">
            <input
              type="checkbox"
              id="sendNow"
              checked={sendNow}
              onChange={(e) => setSendNow(e.target.checked)}
              className="mt-0.5 h-4 w-4 accent-brand-primary"
            />
            <div>
              <label htmlFor="sendNow" className="text-sm font-medium cursor-pointer">
                Emails direkt beim Erstellen versenden
              </label>
              <p className="text-xs text-text-secondary mt-0.5">
                {sendNow
                  ? 'Alle Lieferanten erhalten sofort eine Email mit dem Angebotsportal-Link.'
                  : 'Emails können später manuell über die Anfrage gesendet werden.'}
              </p>
            </div>
          </div>
        </div>
      )}
    </Modal>
  )
}

// ─── Einkauf Settings Modal ───────────────────────────────────────────────────

interface EinkaufSettings {
  deadlineDays: number
  contactCategory: string
  approvalThreshold: number | null
  approverUserIds: string[]
}

interface ApproverUser {
  id: string
  name: string
  email: string
}

function EinkaufSettingsModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { toast } = useToast()
  const queryClient = useQueryClient()

  const { data: settings } = useQuery({
    queryKey: ['rfq-settings'],
    queryFn: () => apiClient.get('/api/einkauf/rfq/settings').then((r) => r.data as EinkaufSettings),
    enabled: open,
    staleTime: 60_000,
  })

  const { data: usersData } = useQuery({
    queryKey: ['users-list'],
    queryFn: () => apiClient.get('/api/users').then((r) => r.data as ApproverUser[]),
    enabled: open,
    staleTime: 60_000,
  })

  const users = usersData ?? []

  const [threshold, setThreshold] = useState<string>('')
  const [approverIds, setApproverIds] = useState<string[]>([])

  // Sync from loaded settings
  if (settings && threshold === '' && (settings.approvalThreshold ?? '') !== '') {
    setThreshold(settings.approvalThreshold !== null ? String(settings.approvalThreshold) : '')
  }
  if (settings && approverIds.length === 0 && settings.approverUserIds.length > 0) {
    setApproverIds(settings.approverUserIds)
  }

  const saveMutation = useMutation({
    mutationFn: () => apiClient.patch('/api/einkauf/rfq/settings', {
      approvalThreshold: threshold.trim() === '' ? null : parseFloat(threshold),
      approverUserIds: approverIds,
    }).then((r) => r.data),
    onSuccess: () => {
      toast({ title: 'Einstellungen gespeichert', tone: 'success' })
      queryClient.invalidateQueries({ queryKey: ['rfq-settings'] })
      onClose()
    },
    onError: (err: Error) => toast({ title: 'Fehler beim Speichern', description: err.message, tone: 'danger' }),
  })

  function toggleApprover(userId: string) {
    setApproverIds((prev) => prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId])
  }

  return (
    <Modal open={open} onClose={onClose} title="Einkauf-Einstellungen" size="md"
      footer={
        <div className="flex justify-between w-full">
          <Button variant="ghost" onClick={onClose}>Abbrechen</Button>
          <Button variant="primary" onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending}>
            {saveMutation.isPending ? 'Speichert…' : 'Speichern'}
          </Button>
        </div>
      }
    >
      <div className="space-y-6">
        <div>
          <p className="text-xs font-semibold text-text-secondary uppercase tracking-wide mb-3">M6 Genehmigungsworkflow</p>
          <div className="space-y-4">
            <Input
              label="Freigabe-Schwellenwert (EUR, netto)"
              type="number"
              min={0}
              step={100}
              value={threshold}
              onChange={(e) => setThreshold(e.target.value)}
              placeholder="z.B. 1000 — leer lassen um Workflow zu deaktivieren"
              hint="Bestellungen über diesem Betrag erfordern Freigabe. Leer = Workflow deaktiviert."
            />

            <div>
              <p className="text-sm font-medium mb-2">Freigeber (erhalten E-Mail bei Überschreitung)</p>
              {users.length === 0 && <p className="text-xs text-text-secondary">Keine Benutzer gefunden.</p>}
              <div className="space-y-1 max-h-48 overflow-y-auto border border-border rounded-lg p-2">
                {users.map((u) => (
                  <label key={u.id} className="flex items-center gap-2 px-2 py-1 hover:bg-surface-secondary rounded cursor-pointer">
                    <input
                      type="checkbox"
                      checked={approverIds.includes(u.id)}
                      onChange={() => toggleApprover(u.id)}
                      className="accent-brand-primary"
                    />
                    <span className="text-sm font-medium">{u.name}</span>
                    <span className="text-xs text-text-secondary">{u.email}</span>
                  </label>
                ))}
              </div>
              {approverIds.length > 0 && (
                <p className="text-xs text-text-secondary mt-1">{approverIds.length} Freigeber konfiguriert</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </Modal>
  )
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export function AnfragenPage() {
  const queryClient = useQueryClient()
  const [createOpen, setCreateOpen] = useState(false)
  const [detailRfqId, setDetailRfqId] = useState<string | null>(null)
  const [statusFilter, setStatusFilter] = useState('all')
  const [settingsOpen, setSettingsOpen] = useState(false)

  const { data, isLoading, error } = useQuery({
    queryKey: ['rfq-list', statusFilter],
    queryFn: () => {
      const url = statusFilter === 'all' ? '/api/einkauf/rfq' : `/api/einkauf/rfq?status=${statusFilter}`
      return apiClient.get(url).then((r) => r.data as { data: Rfq[]; total: number })
    },
    staleTime: 30_000,
  })

  const rfqs = data?.data ?? []

  // Count badges per status for all-data
  const { data: allData } = useQuery({
    queryKey: ['rfq-list', 'all'],
    queryFn: () => apiClient.get('/api/einkauf/rfq').then((r) => r.data as { data: Rfq[]; total: number }),
    staleTime: 30_000,
  })

  const countByStatus = (status: string) => (allData?.data ?? []).filter((r) => r.status === status).length

  const columns: ColumnDef<Rfq>[] = [
    {
      accessorKey: 'rfqNumber',
      header: 'Anfrage #',
      cell: ({ row }) => <span className="font-mono font-medium">#{row.original.rfqNumber}</span>,
      size: 100,
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => (
        <StatusBadge
          status={STATUS_MAP[row.original.status] ?? 'pending'}
          label={STATUS_LABELS[row.original.status] ?? row.original.status}
        />
      ),
      size: 180,
    },
    {
      accessorKey: 'itemCount',
      header: 'Positionen',
      cell: ({ row }) => <span className="text-text-secondary">{row.original.itemCount}</span>,
      size: 100,
    },
    {
      accessorKey: 'supplierCount',
      header: 'Lieferanten',
      cell: ({ row }) => (
        <span className="text-text-secondary">
          {row.original.sentCount}/{row.original.supplierCount} gesendet
          {row.original.quoteCount > 0 && (
            <span className="ml-1 text-xs text-green-600">· {row.original.quoteCount} Angebot{row.original.quoteCount !== 1 ? 'e' : ''}</span>
          )}
        </span>
      ),
      size: 160,
    },
    {
      accessorKey: 'deadlineDate',
      header: 'Frist',
      cell: ({ row }) => {
        const d = new Date(row.original.deadlineDate)
        const isExpired = d < new Date() && row.original.status !== 'bestellt'
        return (
          <span className={isExpired ? 'text-red-600 font-medium' : 'text-text-primary'}>
            {d.toLocaleDateString('de-DE')}
          </span>
        )
      },
      size: 120,
    },
    {
      accessorKey: 'createdAt',
      header: 'Erstellt',
      cell: ({ row }) => <span className="text-text-secondary text-xs">{new Date(row.original.createdAt).toLocaleDateString('de-DE')}</span>,
      size: 110,
    },
    {
      id: 'actions',
      cell: ({ row }) => (
        <IconButton
          icon={<ChevronRight size={14} />}
          aria-label="Details"
          size="xs"
          variant="ghost"
          onClick={(e) => { e.stopPropagation(); setDetailRfqId(row.original.id) }}
        />
      ),
      size: 50,
    },
  ]

  function handleCreated(rfqId: string) {
    queryClient.invalidateQueries({ queryKey: ['rfq-list'] })
    setDetailRfqId(rfqId)
  }

  return (
    <div className="p-6 space-y-6">
      <PageHeader
        title="Anfragen (RFQ)"
        description="Angebotsanfragen erstellen, Preise vergleichen und Bestellungen auslösen"
        actions={
          <div className="flex gap-2">
            <IconButton icon={<Settings size={16} />} aria-label="Einstellungen" variant="secondary" onClick={() => setSettingsOpen(true)} />
            <Button variant="primary" onClick={() => setCreateOpen(true)}>
              <Plus size={16} className="mr-1" /> Neue Anfrage
            </Button>
          </div>
        }
      />

      {/* Status filter tabs */}
      <div className="flex gap-1 flex-wrap border-b border-border pb-0">
        {FILTER_TABS.map((tab) => {
          const count = tab.key === 'all' ? (allData?.total ?? 0) : countByStatus(tab.key)
          return (
            <button
              key={tab.key}
              onClick={() => setStatusFilter(tab.key)}
              className={`px-3 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap -mb-px ${
                statusFilter === tab.key
                  ? 'border-brand-primary text-brand-primary'
                  : 'border-transparent text-text-secondary hover:text-text-primary hover:border-border'
              }`}
            >
              {tab.label}
              {count > 0 && (
                <span className={`ml-1.5 px-1.5 py-0.5 text-xs rounded-full ${
                  statusFilter === tab.key ? 'bg-brand-primary/10 text-brand-primary' : 'bg-surface-secondary text-text-secondary'
                }`}>
                  {count}
                </span>
              )}
            </button>
          )
        })}
      </div>

      <Card>
        <Card.Body>
          {isLoading && <LoadingState />}
          {error && <ErrorState title="Fehler beim Laden" description={(error as Error).message} />}
          {!isLoading && !error && rfqs.length === 0 && (
            <EmptyState
              title={statusFilter === 'all' ? 'Noch keine Anfragen' : `Keine Anfragen mit Status „${STATUS_LABELS[statusFilter] ?? statusFilter}"`}
              description={statusFilter === 'all' ? 'Erstellen Sie Ihre erste Angebotsanfrage.' : ''}
              action={statusFilter === 'all' ? <Button variant="primary" onClick={() => setCreateOpen(true)}><Plus size={14} className="mr-1" /> Erste Anfrage erstellen</Button> : undefined}
            />
          )}
          {!isLoading && rfqs.length > 0 && (
            <DataTable
              columns={columns}
              data={rfqs}
              onRowClick={(row) => setDetailRfqId(row.id)}
              pagination={{ pageSize: 20 }}
            />
          )}
        </Card.Body>
      </Card>

      <CreateAnfrageDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={handleCreated}
      />

      <RfqDetailModal
        rfqId={detailRfqId}
        onClose={() => setDetailRfqId(null)}
      />

      <EinkaufSettingsModal
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
      />
    </div>
  )
}
